import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { timeout } from 'rxjs/operators';
import { getConnection, getCurrentUser, getEndpoint } from 'src/app/core/services/functions.service';

export interface OpItem {
  TRANSPORTE: string | null;
  CLASE_TRANSPORTE: string | null;
  FECHA_PICKING: string | null;
  NUM_PEDIDO_SAP: string | null;
  NUM_ENTREGA_SAP: string | null;
  CLIENTE: string | null;
  TIPO_VEHICULO: string | null;
}

export interface ClienteItem {
  nombre: string;
  codigo: string;
}

export interface TipoVehiculoValor {
  consecutivo: number;
  custom3: string;
}

export interface TipoVehiculoItem {
  nombre: string;
  codigo: string;
  max_consecutivo: number;
  valores: TipoVehiculoValor[];
}

export type FiltroReintento = 'sin_reintento' | 'con_reintento' | 'todos';

export interface HistoricoEnvioItem {
  ID: number;
  NUM_PEDIDO_SEUS: string;
  NUM_ENTREGA_SAP: string;
  SCHEMA_CODE: string;
  CATEGORY: string;
  STATUS: string;
  STATUS_CODE: string;
  DETALLE: string;
  URL: string;
  FECHA_ENVIO: string;
  USUARIO_CREACION: string;
  REINTENTO: string | null;
}

export interface HistoricoEnvioRespuesta {
  success: boolean;
  status: string;
  response: {
    added: string[];
    edited: string[];
    skipped: string[];
  };
}

export interface HistoricoEnvioDetalle extends HistoricoEnvioItem {
  RESPONSE_JSON: string;
}

export interface HistoricoEnvioResumenGrupo {
  response_json: string;
  url: string;
  status_code: string;
  fecha_envio: string;
  repeticiones: number;
}

function mensajeError(err: any, fallback: string): string {
  if (err?.error?.message) return err.error.message;
  const status = err?.status;
  if (status === 0)   return 'No se pudo conectar con el servidor. Verifica tu conexión.';
  if (status === 404) return 'El recurso solicitado no fue encontrado.';
  if (status === 500) return 'Error interno del servidor. Intenta nuevamente.';
  if (status === 403) return 'No tienes permisos para realizar esta acción.';
  if (status === 401) return 'Sesión expirada. Por favor vuelve a iniciar sesión.';
  if (status === 408 || err?.name === 'TimeoutError') return 'La solicitud tardó demasiado. Intenta nuevamente.';
  return fallback;
}

@Injectable({ providedIn: 'root' })
export class GestionarOpsClientesService {

  company = getConnection();

  base_url       = getEndpoint('logisticagestionasignacionops');
  url_entregas   = getEndpoint('logisticaditransa');

  url_transportes        = `${this.base_url}/transportes/${this.company}`;
  url_ops                = `${this.base_url}/${this.company}`;
  url_clientes           = `${this.base_url}/clientes`;
  url_tipo_vehiculo      = `${this.base_url}/tipo_vehiculo`;
  url_actualizar_cliente = `${this.base_url}/actualizar_cliente/${this.company}`;
  url_blanquear_cliente  = `${this.base_url}/blanquear_cliente/${this.company}`;
  url_historico_envio         = `${this.base_url}/historico_envio/${this.company}`;
  url_historico_envio_resumen = `${this.base_url}/historico_envio/${this.company}/response_json`;

  constructor(private http: HttpClient) {}

  async getTransportes(): Promise<{ success: boolean; data: string[]; message: string }> {
    try {
      const resp = await this.http
        .get<{ data: { TRANSPORTE: string }[] }>(this.url_transportes)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data.map((t) => t.TRANSPORTE), message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudieron cargar los transportes.') };
    }
  }

  async getOps(): Promise<{ success: boolean; data: OpItem[]; message: string }> {
    try {
      const resp = await this.http
        .get<{ data: OpItem[] }>(this.url_ops)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudieron cargar las OPs.') };
    }
  }

  async getClientes(): Promise<{ success: boolean; data: ClienteItem[]; message: string }> {
    try {
      const resp = await this.http
        .get<{ data: ClienteItem[] }>(this.url_clientes)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudieron cargar los clientes.') };
    }
  }

  async getTiposVehiculo(): Promise<{ success: boolean; data: TipoVehiculoItem[]; message: string }> {
    try {
      const resp = await this.http
        .get<{ data: TipoVehiculoItem[] }>(this.url_tipo_vehiculo)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudieron cargar los tipos de vehículo.') };
    }
  }

  async actualizarCliente(
    transportes: string[],
    cliente: string,
    tipoVehiculo?: string
  ): Promise<{ success: boolean; message: string; registros_actualizados?: number }> {
    try {
      const body: { transportes: string[]; cliente: string; tipo_vehiculo?: string } = { transportes, cliente };
      if (tipoVehiculo) {
        body.tipo_vehiculo = tipoVehiculo;
      }
      const resp = await this.http
        .put<{ message: string; transportes: string[]; cliente: string; tipo_vehiculo?: string; registros_actualizados: number }>(
          this.url_actualizar_cliente,
          body
        )
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, message: resp.message, registros_actualizados: resp.registros_actualizados };
    } catch (err) {
      return { success: false, message: mensajeError(err, 'No se pudo actualizar el cliente.') };
    }
  }

  async blanquearCliente(
    transportes: string[]
  ): Promise<{ success: boolean; message: string; registros_actualizados?: number }> {
    try {
      const resp = await this.http
        .put<{ message: string; transportes: string[]; registros_actualizados: number }>(
          this.url_blanquear_cliente,
          { transportes }
        )
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, message: resp.message, registros_actualizados: resp.registros_actualizados };
    } catch (err) {
      return { success: false, message: mensajeError(err, 'No se pudo limpiar el cliente.') };
    }
  }

  async getHistoricoEnvio(
    fechaInicio: string,
    fechaFin?: string,
    filtroReintento: FiltroReintento = 'sin_reintento'
  ): Promise<{ success: boolean; data: HistoricoEnvioItem[]; message: string }> {
    try {
      let url = `${this.url_historico_envio}?fecha_inicio=${fechaInicio}`;
      if (fechaFin) {
        url += `&fecha_fin=${fechaFin}`;
      }
      url += `&filtro_reintento=${filtroReintento}`;
      const resp = await this.http
        .get<{ data: HistoricoEnvioItem[] }>(url)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudo cargar el histórico de envío.') };
    }
  }

  async getHistoricoEnvioDetalle(
    id: number
  ): Promise<{ success: boolean; data: HistoricoEnvioDetalle | null; message: string }> {
    try {
      const resp = await this.http
        .get<{ data: HistoricoEnvioDetalle }>(`${this.url_historico_envio}/${id}`)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: null, message: mensajeError(err, 'No se pudo cargar el detalle del envío.') };
    }
  }

  async getHistoricoEnvioResumen(
    fechaInicio: string,
    fechaFin?: string
  ): Promise<{ success: boolean; data: HistoricoEnvioResumenGrupo[]; message: string }> {
    try {
      let url = `${this.url_historico_envio_resumen}?fecha_inicio=${fechaInicio}`;
      if (fechaFin) {
        url += `&fecha_fin=${fechaFin}`;
      }
      const resp = await this.http
        .get<{ data: HistoricoEnvioResumenGrupo[] }>(url)
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, data: resp.data, message: '' };
    } catch (err) {
      return { success: false, data: [], message: mensajeError(err, 'No se pudo cargar el resumen del histórico de envío.') };
    }
  }

  async enviarOpsManual(): Promise<{ success: boolean; message: string }> {
    try {
      const usuario = getCurrentUser().usuario_aplicativo || '';
      const resp = await this.http
        .post<{ success: boolean; message: string }>(this.url_entregas, { usuario })
        .pipe(timeout(29000))
        .toPromise();
      return { success: true, message: resp.message };
    } catch (err) {
      return { success: false, message: mensajeError(err, 'No se pudo iniciar el proceso de envío.') };
    }
  }
}
